import { cache, getRenderingEngine } from "@cornerstonejs/core";
import type { Point3 } from "@cornerstonejs/core/dist/esm/types";
import {
  addTool,
  annotation as csAnnotation,
  ProbeTool,
  ToolGroupManager,
} from "@cornerstonejs/tools";

import getIds from "lib/cornerstone/helpers/getIds";
import viewerState from "stores/viewer";
import { statusColor } from "./microbleedStatus";
import type { Microbleed } from "./useMicrobleeds";

// ── Coordinate convention ───────────────────────────────────────
// The backend `coordinateMm` is a WORLD coordinate in NIfTI's RAS convention.
// Cornerstone3D works in DICOM/LPS world. RAS -> LPS = negate X and Y, keep Z.
//
// We use the mm world coordinate (NOT the voxel index): the nifti-volume-loader
// reorients the voxel grid on load, so the backend voxel index no longer maps
// through Cornerstone's indexToWorld to the right place — but the world mm is
// grid-independent and just needs the RAS->LPS flip.
//
// If your dots ever land on the correct slice but MIRRORED left<->right, your
// loader is keeping RAS; set this to false.
const MM_IS_RAS = true;

function mmToWorld(mm: [number, number, number]): Point3 {
  return MM_IS_RAS ? [-mm[0], -mm[1], mm[2]] : [mm[0], mm[1], mm[2]];
}

// Fallback substring used only when no exact volumeId is provided.
const REF_VOLUME_HINT = "segm-cmb_spatial-analysis";

const addedUIDs: string[] = [];
// SINGLE SOURCE OF TRUTH for each microbleed's world position.
const worldByUid = new Map<string, Point3>();

const rgb = ([r, g, b]: [number, number, number]) => `rgb(${r},${g},${b})`;

function rerender() {
  const ids = getIds(viewerState.viewerId ?? undefined);
  const re = getRenderingEngine(ids.renderingEngineId);
  re?.renderViewports([ids.viewportId1, ids.viewportId2, ids.viewportId3]);
}

export function clearMicrobleedPoints() {
  for (const uid of addedUIDs) {
    try {
      csAnnotation.state.removeAnnotation(uid);
    } catch {
      /* ignore */
    }
  }
  addedUIDs.length = 0;
  worldByUid.clear();
  rerender();
}

/** Resolve a microbleed's Cornerstone world position. Prefer mm (grid-free). */
function microbleedWorld(mb: Microbleed, refVolume: any): Point3 | null {
  if (mb.coordinateMm) return mmToWorld(mb.coordinateMm);
  // Legacy fallback: voxel via indexToWorld (only correct if grid not reoriented).
  if (mb.coordinateVoxel && refVolume?.imageData?.indexToWorld) {
    return refVolume.imageData.indexToWorld(mb.coordinateVoxel) as Point3;
  }
  return null;
}

/**
 * Draw one colored point per microbleed at its world coordinate, and remember
 * that world coordinate so focus uses the exact same one.
 *
 * @param refVolumeId  EXACT volumeId of THIS session's spatial volume
 *                     (`nifti:${spatialVolumeUrl}`). Only used for the legacy
 *                     voxel fallback when a microbleed has no mm coordinate.
 */
export function showMicrobleedPoints(
  microbleeds: Microbleed[],
  refVolumeId?: string | null,
) {
  const ids = getIds(viewerState.viewerId ?? undefined);
  const re = getRenderingEngine(ids.renderingEngineId);
  if (!re) return;

  const vp = re.getViewport(ids.viewportId1) as any;
  if (!vp?.getFrameOfReferenceUID) return;

  const refVolume = (
    refVolumeId
      ? cache.getVolume(refVolumeId)
      : cache.getVolumes().find((v) => v.volumeId.includes(REF_VOLUME_HINT))
  ) as any;

  // Make sure Probe is registered and Enabled (renders, no interaction).
  try {
    addTool(ProbeTool);
  } catch {
    /* already registered globally */
  }
  const tg = ToolGroupManager.getToolGroup(ids.toolGroupId);
  if (tg && !(tg as any)._toolInstances?.[ProbeTool.toolName]) {
    tg.addTool(ProbeTool.toolName);
  }
  tg?.setToolEnabled(ProbeTool.toolName);

  const FrameOfReferenceUID = vp.getFrameOfReferenceUID();
  const { viewPlaneNormal } = vp.getCamera();

  clearMicrobleedPoints();

  for (const mb of microbleeds) {
    const world = microbleedWorld(mb, refVolume);
    if (!world) continue;
    worldByUid.set(mb.uid, world);

    const uid = `mb-${mb.uid}`;
    const ann = {
      annotationUID: uid,
      highlighted: false,
      invalidated: true,
      isLocked: false,
      isVisible: true,
      metadata: {
        toolName: ProbeTool.toolName,
        viewPlaneNormal: [...viewPlaneNormal] as Point3,
        FrameOfReferenceUID,
        referencedImageId: undefined,
      },
      data: {
        label: `microbleed_${String(mb.number ?? "").padStart(2, "0")}`,
        handles: { points: [world] },
        cachedStats: {},
      },
    };

    csAnnotation.state.addAnnotation(ann as any, FrameOfReferenceUID);
    addedUIDs.push(uid);

    const color = rgb(statusColor(mb.status));
    csAnnotation.config.style.setAnnotationStyles(uid, {
      color,
      colorHighlighted: color,
      colorSelected: color,
      colorLocked: color,
    } as any);
  }

  rerender();
}

/**
 * Move all three viewports + the crosshair center to a world position.
 *
 * Camera is repositioned by TRANSLATING it by the focal-point delta (preserves
 * the exact position<->focalPoint vector: distance, direction, zoom), and the
 * crosshair center is written directly. The previous version also called
 * CrosshairsTool.setToolCenter(), which re-scrolls relative to its own stored
 * center; combined with the manual move it double-applied the delta and, after
 * a couple of focuses, pushed the cameras into empty space => black viewports.
 */
function moveCamerasTo(worldPos: Point3) {
  const ids = getIds(viewerState.viewerId ?? undefined);
  const re = getRenderingEngine(ids.renderingEngineId);
  if (!re) return;

  const viewportIds = [ids.viewportId1, ids.viewportId2, ids.viewportId3];
  for (const vpId of viewportIds) {
    const vp = re.getViewport(vpId) as any;
    if (!vp) continue;
    const { focalPoint, position } = vp.getCamera();
    const dx = worldPos[0] - focalPoint[0];
    const dy = worldPos[1] - focalPoint[1];
    const dz = worldPos[2] - focalPoint[2];
    vp.setCamera({
      focalPoint: worldPos,
      position: [
        position[0] + dx,
        position[1] + dy,
        position[2] + dz,
      ] as Point3,
    });
  }

  // Update the crosshair center DIRECTLY. We deliberately do NOT call
  // setToolCenter(): it scrolls each viewport relative to the tool's own stored
  // center, so combined with the manual camera move above it double-applies the
  // delta — after a couple of focuses that throws the cameras into empty space
  // (the black viewports you saw). Writing toolCenter just refreshes the
  // reference lines on the next render without moving any camera.
  const tg = ToolGroupManager.getToolGroup(ids.toolGroupId);
  const tool = tg?.getToolInstance("Crosshairs") as any;
  if (tool) {
    tool.toolCenter = [worldPos[0], worldPos[1], worldPos[2]] as Point3;
  }

  re.renderViewports(viewportIds);
}

/**
 * Focus a microbleed by uid. Uses the world stored when the dot was drawn, so
 * focus and dot always coincide. If the dot isn't drawn yet, falls back to the
 * mm coordinate (preferred) or the exact-volume voxel conversion.
 */
export function focusMicrobleed(
  uid: string,
  fallback?: {
    mm?: [number, number, number] | null;
    voxel?: [number, number, number] | null;
    refVolumeId?: string | null;
  },
) {
  let world = worldByUid.get(uid) ?? null;

  if (!world && fallback) {
    if (fallback.mm) {
      world = mmToWorld(fallback.mm);
    } else if (fallback.voxel && fallback.refVolumeId) {
      const vol = cache.getVolume(fallback.refVolumeId) as any;
      if (vol?.imageData?.indexToWorld) {
        world = vol.imageData.indexToWorld(fallback.voxel) as Point3;
      }
    }
  }

  if (!world) return;
  moveCamerasTo(world);
}
